import type { RelevantYieldEvents } from "@omni/ads";
import type { Keywords } from "./getKeywords";

import { getApnTag, getBiddingSdk, getUserIds } from ".";

const keys = ["aa-sch-publisher", "se-omni-section", "se-generic-section"];

const extractKeysAndValues = (keywords: Keywords) => {
  return keys.reduce((keyValues, key) => {
    if (keywords[key]) {
      return { ...keyValues, [key]: keywords[key] };
    }

    return keyValues;
  }, {});
};

const prebidConfigId = process.env.NEXT_PUBLIC_PREBID_CONFIG_ID;

const loadAds = async (params: string[], keywords: Keywords) => {
  const apntag = await getApnTag();
  const biddingSdk = await getBiddingSdk();
  const userIds = await getUserIds();

  let cancel = false;

  const events = {
    relevantCb: {
      timeout: 1000,
      done: false,
    },
    auctionCb: {
      timeout: 2000,
      done: false,
    },
  };

  const failsafeOnTimeout = (ev: keyof RelevantYieldEvents) =>
    setTimeout(() => {
      if (!events[ev].done) {
        console.error(
          `Doing failsafe loadTags() call after waiting too long for '${ev}'`,
        );
        cancel = true;
        apntag.loadTags();
      }
    }, events[ev].timeout);

  const relevantCbTimeout = failsafeOnTimeout("relevantCb");

  return new Promise<void>((resolve, reject) => {
    biddingSdk.cmd.push(function () {
      if (cancel) {
        return reject(new Error("Auction cancelled by failsafe."));
      }
      clearTimeout(relevantCbTimeout);
      events.relevantCb.done = true;
      if (!biddingSdk.loadPrebid) {
        console.error(`Calling loadTags() without Relevant`);
        apntag.anq = apntag.anq || [];
        apntag.anq.push(() => {
          apntag.loadTags();
          resolve(); // resolve when loadTags is called
        });
        return;
      }
      const auctionCbTimeout = failsafeOnTimeout("auctionCb");
      biddingSdk.addPrebidConfig({
        consentManagement: {
          gdpr: {
            cmpApi: "iab",
            timeout: 8000,
          },
        },
        enableSendAllBids: false,
        userSync: {
          userIds,
        },
        ortb2: {
          site: {
            ext: {
              data: extractKeysAndValues(keywords),
            },
          },
        },
      });
      biddingSdk.loadPrebid({
        configId: `${prebidConfigId}`,
        manageAdserver: false,
        loadUnknownSlots: true,
        noSlotReload: false,
        noGpt: true,
        apntagCalls: {
          showTag: () => {},
          refresh: () => {},
          loadTags: (args: string[]) => {
            if (!cancel) {
              clearTimeout(auctionCbTimeout);
              events.auctionCb.done = true;
              apntag.loadTags(args);
              resolve(); // Only resolve when loadTags is called
            }
          },
        },
        ...params,
      });
    });
  });
};

export { loadAds };
